/**
 * Familiarisation Template Helpers
 * Migration and utility functions for multi-template system
 */

import { FAMILIARISATION_SECTIONS, BASE_ITEMS } from '../config/familiarisationItems5Sections';
import { CONTRACTOR_TEMPLATE, SECTION_PRESETS } from '../config/familiarisationTemplates';

/**
 * Build the default crew template from existing base items
 */
export function buildDefaultTemplate(customItems = [], editedItems = {}, deletedItems = [], settings = {}) {
  const sections = [];

  // Map existing sections to new format
  Object.entries(FAMILIARISATION_SECTIONS).forEach(([sectionId, sectionDef]) => {
    const baseItems = BASE_ITEMS[sectionId] || [];

    const items = baseItems.map(item => ({
      id: item.id,
      text: editedItems[item.id] || item.item,
      required: item.classification === 'MANDATORY',
      regulation: item.wbc3Ref || 'WBC3 App 8',
      isCustom: false,
      isEdited: !!editedItems[item.id],
      originalText: editedItems[item.id] ? item.item : null,
      roles: item.roles,
      priority: item.priority
    })).filter(item => !deletedItems.includes(item.id));

    // Add custom items for this section
    const sectionCustomItems = customItems
      .filter(ci => ci.section === sectionId)
      .map(ci => ({
        id: ci.id,
        text: ci.text,
        required: false,
        regulation: 'Company',
        isCustom: true,
        isEdited: false,
        originalText: null,
        roles: ci.roles || ['all'],
        priority: ci.priority || 'normal'
      }));

    sections.push({
      id: sectionId,
      name: sectionDef.title,
      order: Object.keys(FAMILIARISATION_SECTIONS).indexOf(sectionId) + 1,
      items: [...items, ...sectionCustomItems]
    });
  });

  return {
    id: 'fcl_default',
    name: 'Crew Familiarisation',
    type: 'crew',
    isDefault: true,
    isSystem: true,
    validityPeriodMonths: settings.validityPeriodMonths || 12,
    appliesTo: {
      positions: ['all'],
      departments: ['all']
    },
    sections,
    customItems: [],  // Migrated into sections
    editedItems: {},  // Migrated into items
    deletedItems: [], // Applied during build
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Build the contractor template with timestamps
 */
export function buildContractorTemplate() {
  return {
    ...CONTRACTOR_TEMPLATE,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Migrate existing familiarisation data to v2 template structure
 * @param {string} vesselId - Current vessel ID
 * @returns {object} - New templates data structure
 */
export function migrateFamiliarisationToV2(vesselId) {
  const MIGRATION_KEY = `familiarisation_migrated_v2_${vesselId}`;

  // Check if already migrated
  const existingMigration = localStorage.getItem(MIGRATION_KEY);
  if (existingMigration) {
    console.log('[FAM Migration] Already migrated for vessel', vesselId);
    return null;
  }

  console.log('[FAM Migration] Starting migration for vessel', vesselId);

  // Read legacy storage
  const customItemsRaw = localStorage.getItem(`customFamiliarisationItems_${vesselId}`);
  const editedItemsRaw = localStorage.getItem(`editedFamiliarisationItems_${vesselId}`);
  const deletedItemsRaw = localStorage.getItem(`deletedFamiliarisationItems_${vesselId}`);
  const settingsRaw = localStorage.getItem(`familiarisationSettings_${vesselId}`);

  const customItems = customItemsRaw ? JSON.parse(customItemsRaw) : [];
  const editedItems = editedItemsRaw ? JSON.parse(editedItemsRaw) : {};
  const deletedItems = deletedItemsRaw ? JSON.parse(deletedItemsRaw) : [];
  const settings = settingsRaw ? JSON.parse(settingsRaw) : {};

  // Also check unscoped legacy keys
  const legacyCustomItems = localStorage.getItem('customFamiliarisationItems');
  if (legacyCustomItems && customItems.length === 0) {
    try {
      const parsed = JSON.parse(legacyCustomItems);
      customItems.push(...parsed);
    } catch (e) {
      console.warn('[FAM Migration] Failed to parse legacy customItems', e);
    }
  }

  // Build templates
  const defaultTemplate = buildDefaultTemplate(customItems, editedItems, deletedItems, settings);
  const contractorTemplate = buildContractorTemplate();

  const templatesData = {
    version: 1,
    lastUpdated: new Date().toISOString(),
    templates: [defaultTemplate, contractorTemplate]
  };

  // Write new structure
  localStorage.setItem(
    `familiarisation_checklist_templates_${vesselId}`,
    JSON.stringify(templatesData)
  );

  // Mark as migrated
  localStorage.setItem(MIGRATION_KEY, new Date().toISOString());

  console.log('[FAM Migration] Migration complete. Created', templatesData.templates.length, 'templates');

  return templatesData;
}

/**
 * Get a template by ID
 */
export function getTemplateById(templatesData, templateId) {
  if (!templatesData?.templates) return null;
  return templatesData.templates.find(t => t.id === templateId);
}

/**
 * Get the default template
 */
export function getDefaultTemplate(templatesData) {
  if (!templatesData?.templates) return null;
  return templatesData.templates.find(t => t.isDefault) || templatesData.templates[0];
}

/**
 * Get templates applicable to a position
 */
export function getTemplatesForPosition(templatesData, position) {
  if (!templatesData?.templates) return [];

  return templatesData.templates.filter(t => {
    if (t.appliesTo?.positions?.includes('all')) return true;
    if (t.appliesTo?.positions?.includes(position?.toLowerCase())) return true;
    return false;
  });
}

/**
 * Calculate total items in a template
 */
export function getTemplateItemCount(template) {
  if (!template?.sections) return 0;
  return template.sections.reduce((sum, section) => sum + (section.items?.length || 0), 0);
}

/**
 * Validate template structure
 */
export function validateTemplate(template) {
  const errors = [];

  if (!template.id) errors.push('Template must have an ID');
  if (!template.name) errors.push('Template must have a name');
  if (!template.type) errors.push('Template must have a type');
  if (!Array.isArray(template.sections)) errors.push('Template must have sections array');

  template.sections?.forEach((section, i) => {
    if (!section.id) errors.push(`Section ${i} must have an ID`);
    if (!section.name) errors.push(`Section ${i} must have a name`);
    if (!Array.isArray(section.items)) errors.push(`Section ${i} must have items array`);
  });

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Generate a unique template ID
 */
export function generateTemplateId() {
  return `fcl_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
}

/**
 * Generate a unique section ID
 */
export function generateSectionId() {
  return `sec_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
}

/**
 * Generate a unique item ID
 */
export function generateItemId() {
  return `item_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
}
